import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { gql } from "../../../../__generated__";
import { formatDateToYYYYMMDDhhmm } from "../../../../utils/date/format-date";
import { getOrderType } from "../../../../utils/order";
import LoadingSpinnerFull from "../../../../components/common/loading-spinner/loading-spinner-full";
import { OrderStatus } from "../../../../components/order/order-status";
import Header from "../../../../components/common/header";
import { GetStoreOrdersQuery, Order } from "../../../../__generated__/graphql";

const GET_STORE_ORDERS = gql(`
  query GetStoreOrders($type: String!, $status: String!, $startDate: String!, $endDate: String!) {
    getStoreOrders(type: $type, status: $status, startDate: $startDate, endDate: $endDate) {
      id
      sellerAmount
      sellerAmountWithFee
      createdAt
      isPackConfirmed
      isCanceled
      packId
      totalQuantity
      pack { 
        id 
      }
      products { 
        id 
      }
    }
  }
`);

const SettlementSummary = ({ orders }: { orders: Order[] }) => {
  // 총 판매 금액
  const totalAmount = orders.reduce(
    (sum: number, order: Order) => sum + (order.sellerAmount || 0),
    0
  );

  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl shadow-sm">
      <div className="text-xl font-bold text-blue-800">
        총 판매 금액: {totalAmount.toLocaleString()}원
      </div>
      <div className="text-blue-600 mt-2">검색 결과: {orders.length}건</div>
    </div>
  );
};

export default function MyRevenue() {
  const [type, setType] = useState<string>("ALL"); // 팩 유형 필터 (기본값: 모두보기)
  const [status, setStatus] = useState<string>("CONFIRMED"); // 주문 확정여부 필터 (기본값: 확정)
  const [period, setPeriod] = useState<string>("today"); // 기간 필터 (기본값: 오늘)
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc"); // 정렬 순서 (오름차순, 내림차순)

  const today = new Date();
  const [startDate, setStartDate] = useState<string>(
    today.toISOString().slice(0, 10) // 날짜만 추출
  ); // 시작 날짜
  const [endDate, setEndDate] = useState<string>(
    today.toISOString().slice(0, 10) // 날짜만 추출
  ); // 종료 날짜

  // 기간 필터 변경 시 날짜 설정
  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
    const today = new Date();

    switch (newPeriod) {
      case "today":
        const todayStr = today.toISOString().slice(0, 10); // 날짜만 추출
        setStartDate(todayStr);
        setEndDate(todayStr);
        break;
      case "week":
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 6); // 오늘 포함 최근 7일
        setStartDate(weekAgo.toISOString().slice(0, 10)); // 날짜만 추출
        setEndDate(today.toISOString().slice(0, 10)); // 날짜만 추출
        break;
      case "month":
      case "custom": // 직접 날짜 선택 (기본값은 오늘부터 최근 30일을 따름)
        const monthAgo = new Date();
        monthAgo.setDate(monthAgo.getDate() - 29); // 오늘 포함 최근 30일
        setStartDate(monthAgo.toISOString().slice(0, 10)); // 날짜만 추출
        setEndDate(today.toISOString().slice(0, 10)); // 날짜만 추출
        break;
    }
  };

  // 날짜 유효성 검사
  const isValidDate = (dateString: string) => {
    return Boolean(dateString) && !isNaN(new Date(dateString).getTime());
  };

  const validStart = isValidDate(startDate); // 시작 날짜 유효성 검사
  const validEnd = isValidDate(endDate); // 종료 날짜 유효성 검사

  const { data, loading, refetch } = useQuery<GetStoreOrdersQuery>(
    GET_STORE_ORDERS,
    {
      variables:
        validStart && validEnd
          ? {
              type, // 팩 유형 필터
              status, // 주문 확정여부 필터
              startDate: new Date(startDate + "T00:00:00").toISOString(), // 시작 날짜 (포함)
              endDate: new Date(endDate + "T23:59:59").toISOString(), // 종료 날짜 (포함)
            }
          : undefined,
      skip: !validStart || !validEnd, // 유효하지 않은 날짜일 경우 쿼리 스킵
      onCompleted: () => {
        console.log(type, status, startDate, endDate);
      },
    }
  );

  const handleRefetch = useCallback(() => {
    if (!isValidDate(startDate) || !isValidDate(endDate)) {
      alert("올바른 날짜를 선택하세요.");
      return;
    }

    refetch({
      type,
      status,
      startDate: new Date(startDate + "T00:00:00").toISOString(),
      endDate: new Date(endDate + "T23:59:59").toISOString(),
    });
  }, [refetch, startDate, endDate, type, status]);

  // 필터 변경 시 자동 검색
  useEffect(() => {
    if (validStart && validEnd) {
      handleRefetch();
    }
  }, [
    type,
    status,
    period,
    startDate,
    endDate,
    handleRefetch,
    validStart,
    validEnd,
  ]);

  const handleSort = useCallback(() => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  }, [sortOrder]);

  // 정렬된 주문 목록
  const sortedOrders: Order[] = data?.getStoreOrders
    ? [...data.getStoreOrders]
        .filter((order): order is Order => order !== null) // 타입 가드
        .sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
        })
    : [];

  return (
    <div className="w-full min-h-screen flex flex-col">
      <Header title="우리가게 매출" showBackButton />
      <div className="p-4 flex-1 overflow-y-auto">
        {/* 기간 필터 */}
        <div className="mb-6">
          <div className="font-semibold text-gray-700 mb-2">기간</div>
          <div className="flex flex-wrap gap-3 md:gap-3">
            {["today", "week", "month", "custom"].map((p) => (
              <button
                key={p}
                onClick={() => handlePeriodChange(p)}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                  period === p
                    ? "bg-blue-500 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm"
                }`}
              >
                {p === "today"
                  ? "오늘"
                  : p === "week"
                  ? "최근 7일"
                  : p === "month"
                  ? "최근 30일"
                  : "직접 날짜 선택"}
              </button>
            ))}
          </div>
        </div>

        {period === "custom" && (
          <div className="mb-6 flex gap-2 items-center">
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                const newStart = e.target.value;
                setStartDate(newStart);
                if (newStart > endDate) {
                  setEndDate(newStart);
                }
              }}
              max={endDate}
              className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="text-gray-500 font-medium">~</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                const newEnd = e.target.value;
                if (newEnd < startDate) {
                  alert("종료일은 시작일보다 빠를 수 없습니다.");
                  return;
                }
                setEndDate(newEnd);
              }}
              min={startDate}
              className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        )}

        <div className="mb-6">
          <div className="font-semibold text-gray-700 mb-2">팩 유형</div>
          <div className="flex flex-wrap gap-3">
            {["ALL", "PACK", "PRODUCT"].map((t) => (
              <button
                key={t}
                onClick={() => setType(t)}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                  type === t
                    ? "bg-blue-500 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm"
                }`}
              >
                {t === "ALL"
                  ? "모두보기"
                  : t === "PACK"
                  ? "예약팩"
                  : "실시간팩"}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <div className="font-semibold text-gray-700 mb-2">주문 확정여부</div>
          <div className="flex flex-wrap gap-3">
            {["CONFIRMED", "CANCELED"].map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                  status === s
                    ? "bg-blue-500 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm"
                }`}
              >
                {s === "CONFIRMED" ? "확정" : "취소"}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <LoadingSpinnerFull />
        ) : data?.getStoreOrders && data.getStoreOrders.length > 0 ? (
          <>
            <SettlementSummary orders={sortedOrders} />
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <th
                      className="px-1.5 md:px-3 py-4 text-left text-sm md:text-md font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors"
                      onClick={handleSort}
                    >
                      <div className="flex items-center gap-2">
                        주문날짜
                        <span className="text-sm">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                      </div>
                    </th>
                    <th className="px-1.5 py-4 text-left text-sm md:text-md font-semibold text-gray-700">
                      팩 유형
                    </th>
                    <th className="px-1.5 py-4 text-left text-sm md:text-md font-semibold text-gray-700">
                      확정여부
                    </th>
                    <th className="px-1.5 py-4 text-left text-sm md:text-md font-semibold text-gray-700">
                      수량
                    </th>
                    <th className="px-1.5 py-4 text-left text-sm md:text-md font-semibold text-gray-700">
                      판매금액
                    </th>
                    <th className="px-1.5 py-4 text-left text-sm md:text-md font-semibold text-gray-700">
                      정산금액
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedOrders.map((order: Order) => (
                    <tr
                      key={order.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-1.5 md:px-3 py-4 text-sm md:text-md text-gray-700">
                        {formatDateToYYYYMMDDhhmm(new Date(order.createdAt))}
                      </td>
                      <td className="px-1.5 py-4">
                        <span
                          className={`inline-flex px-1.5 py-1 text-sm md:text-md font-medium rounded-full ${
                            getOrderType(order) === "예약구매"
                              ? "bg-green-100 text-green-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {getOrderType(order) === "예약구매"
                            ? "예약팩"
                            : "실시간팩"}
                        </span>
                      </td>
                      <td className="px-1.5 py-4 text-sm md:text-md">
                        <OrderStatus order={order} />
                      </td>
                      <td className="px-1.5 py-4 text-sm md:text-md text-gray-700 text-center">
                        {order.totalQuantity || 0}개
                      </td>
                      <td className="px-1.5 py-4 text-sm md:text-md text-gray-900 font-semibold">
                        {order.sellerAmount?.toLocaleString()}원
                      </td>
                      <td className="px-1.5 py-4 text-sm md:text-md text-gray-900 font-semibold">
                        {order.sellerAmountWithFee?.toLocaleString() || "-"}원
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <div className="text-lg font-medium">검색 결과가 없습니다.</div>
            <div className="text-md mt-2">다른 조건으로 검색해볼까요?</div>
          </div>
        )}
      </div>
    </div>
  );
}
